// eslint-disable-next-line no-unused-vars
import jsdocTypes from '../../../../../jsdoc-types';

const EventBusConfig = {
  commands: {
    modalSizeSelector: {
      /**
       * Changing sizes in modal size selector
       */
      changeSize: 'modalSizeSelector:changeSize',
      /**
       * Set props in modal size selector
       */
      setProps: 'modalSizeSelector:setProps',
      /**
       * Closed modal size selector
       */
      closed: 'modalSizeSelector:closed',
      /**
       * Closed modal stock alert
       */
      closedStockAlert: 'modalSizeSelector:closedStockAlert',
      /**
       * Create the instances for rendering the modal size selector
       */
      createInstance: 'modalSizeSelector:createInstance',
      /**
       * Remove the Vue instances of the modal size selector
       */
      removeInstance: 'modalSizeSelector:removeInstance',
    },
    cart: {
      /**
       * Create the instances for rendering the recommended product for the cart
       */
      createRecommendationVueInstances: 'cart:createRecommendationVueInstances',
      /**
       * Remove the Vue instances of the recommended products of the cart before
       * the cart content change in order to free memory (event listeners that
       * will block removed nodes...)
       */
      removeRecommendationVueInstances: 'cart:removeRecommendationVueInstances',
      /**
       * Remove from cart all product line items related with a product.
       * Event requires payload.
       * @param {string} variationGroupId
       */
      removeAllSizesOfProduct:
        'cart:removeAllProductLineItemsForVariationGroup',
    },
    TheProductCustomizer: {
      removeComplement: 'complement:remove',
    },
    TheProductDetails: {
      lookSizeSelector: 'the-product-details:lock-size-selector',
      resetSizeSelector: 'the-product-details:reset-size-selector',
      /**
       * Update a product with the given product variation
       * @param {object} payload
       * @param {jsdocTypes.FullProduct} payload.product - product variation with updated data
       * @param {Element} payload.container - DOM element which contains the product
       */
      updateProduct: 'the-product-details:update-product',
    },
    TheProductList: {
      /** Reset the filter selector */
      clearFilters: 'the-product-list:clear-filters',
      /** Open the filter selector */
      openFilters: 'the-product-list:open-filters',
    },
    TheSearch: {
      hide: 'the-search:hide',
      show: 'the-search:show',
      toggle: 'the-search:toggle',
      search: 'the-search:search',
    },
  },
  events: {
    BaseColorSwatches: {
      /**
       * Event triggered after selecting a color from Base color swatches
       * @param {string} color
       */
      colorSelected: 'base-color-swatches:select-color',
    },
    BaseProductSlider: {
      /**
       * Event triggered after initialing Swiper (or another lib) and having
       * a final DOM for the slider
       * @param {Object} payload
       * @param {string[]} payload.productIds the IDs of the products of the
       *  slider
       * @param {Object[]} payload.products Products listed
       * @param {Object} payload.products.display Display config for a product
       * @param {Types.FullProduct} payload.products.product Products listed
       * @param {Object} payload.products.urls URLS of the product
       * @param {jsdocTypes.BaseProductTileContext} payload.tileContext the
       *  context applied to its children (BaseProductTile instances)
       *  (see FRONT_CONFIG)
       */
      ready: 'base-product-slider:ready',
      /**
       * Event triggered when the products of a product slider changed
       * @param {Object} payload
       * @param {string[]} payload.productIds the IDs of the products of the
       *  slider after the update
       * @param {Object[]} payload.products Products listed
       * @param {Object} payload.products.display Display config for a product
       * @param {jsdocTypes.FullProduct} payload.products.product Products listed
       * @param {Object} payload.products.urls URLS of the product
       * @param {jsdocTypes.BaseProductTileContext} payload.tileContext the
       *  context applied to its children (BaseProductTile instances)
       *  (see FRONT_CONFIG)
       */
      updated: 'base-product-slider:updated',
    },
    BaseProductTileQuickShop: {
      /**
       * Event triggered after adding a size to cart by means of Quick Shop
       * It has a large payload: { productVariation, cartAddProduct }.
       * @param {Object} payload
       * @param {Object} payload.productVariation attributeSelect() response
       * @param {jsdocTypes.FullProduct} payload.productVariation.product attributeSelect() product
       * @param {Object} payload.productVariation.resources Useful messages
       * @param {Object} payload.cartAddProduct Cart-AddProduct response
       * @param {jsdocTypes.Cart} payload.cartAddProduct.cart Cart model
       * @param {jsdocTypes.FullProduct} payload.cartAddProduct.product Variant full product
       * @param {string} payload.cartAddProduct.pliUUID UUID of the added line item
       */
      addedToCart: 'quickShop:afterAddSizeToCart',
      /**
       * Event triggered after adding a size to cart by means of Quick Shop
       * It has an ID as payload.
       * @param {string} variantID
       */
      removedFromCart: 'quickShop:afterRemoveSizeFromCart',
    },
    /**
     * Event triggered when the page title is loaded in the main entrypoints
     * @param {object} payload
     * @param {string} payload.pageTitle - page title
     * @param {string} payload.pageType - page type
     */
    pageTitleLoaded: 'page-title:loaded',
    router: {
      /**
       * Event triggered after the navigation guards have finished. Just before
       * the DOM updates are triggered to show the new page
       * @param {object} payload
       * @param {import("vue-router").Route} payload.to - route location
       * @param {import("vue-router").Route} payload.from - route location
       */
      willNavigate: 'vue-router:will-navigate',
    },
    TheProductDetails: {
      addedToCart: 'the-product-details:size-added-to-cart',
      colorSelected: 'the-product-details:select-color',
      /**
       * Event triggered after loading a Product Detail Page.
       * It has the product viewed as payload.
       * @param {jsdocTypes.FullProduct} product
       */
      loaded: 'the-product-details:loaded',
      zoomClosed: 'the-product-details-image-zoom-close',
      zoomOpened: 'the-product-details-image-zoom',
    },
    TheProductList: {
      /**
       * Event triggered after changing the category of PLP.
       * It has the new category object as payload.
       * @param {object} category
       */
      categoryChanged: 'the-product-list:category-changed',
      columnModeChanged: 'the-product-list:column-mode-changed',
      columnModeWillChange: 'the-product-list:column-mode-will-change',
    },
    TheSearch: {
      hidden: 'the-search:hidden',
      /**
       * Event triggered after new results are found
       * @param {object} payload
       * @param {object} payload.search Data related to search
       * @param {string} payload.search.query The search term introduced by user
       * @param {string} payload.search.match The match of the search query
       * @param {number} payload.search.count The number of results
       * @param {number} payload.search.page The page number
       * @param {boolean} payload.search.isCached True if the search results
       * comes from client's cache
       * @param {object[]} payload.products Products listed
       * @param {object} payload.products.display Products listed
       * @param {jsdocTypes.FullProduct} payload.products.product Products listed
       * @param {Object} payload.products.urls URLS of the product
       * @param {boolean} payload.isPageView True if it is the first tracking
       * event of the search (ex: when closing the pdp modal in mobile we
       * consider a new search view)
       */
      searched: 'the-search:searched',
      shown: 'the-search:shown',
      toggled: 'the-search:toggled',
    },
    TheSearchModal: {
      /**
       * Event triggered after recommended product is clicked
       * @param {object} payload The payload of the event
       * @param {object} payload.product Data related to product
       * @param {number} payload.index Product position in carousel
       * @param {string} payload.componentType The type of component
       */
      recommendedProductClicked: 'search-modal-recommended-product:clicked',
      /**
       * Event triggered after recommended product is listed
       * @param {object} payload The payload of the event
       * @param {object} payload.product Data related to product
       * @param {number} payload.index Product position in carousel
       * @param {string} payload.componentType The type of component
       */
      recommendedProductViewed: 'search-modal-recommended-product:viewed',
    },
  },
};

export default EventBusConfig;
